import App from './App.jsx'
import { allToolConfigs } from './services/toolConfigs'
import { guideSlugs } from './services/guides'

/**
 * Adapt a module with a `default` export to React Router's data-router
 * `lazy` convention (which expects a `Component` named export). Using `lazy`
 * means each page is only imported when its route is matched/prerendered,
 * so build-time SSG never imports browser-only pages it isn't rendering.
 */
const lazyDefault = (importer) => async () => {
  const m = await importer()
  return { Component: m.default }
}

// Every valid tool slug (convert + compress + tools). Single source of truth
// for both the :conversionPair static paths and the sitemap.
export const toolSlugs = Object.keys(allToolConfigs)

export const routes = [
  {
    path: '/',
    element: <App />,
    entry: 'src/App.jsx',
    children: [
      { index: true, lazy: lazyDefault(() => import('./pages/Home.jsx')) },

      // Core marketing / legal pages (pre-rendered)
      { path: 'pricing', lazy: lazyDefault(() => import('./pages/Pricing.jsx')) },
      { path: 'supported-formats', lazy: lazyDefault(() => import('./pages/SupportedFormats.jsx')) },
      { path: 'about', lazy: lazyDefault(() => import('./pages/About.jsx')) },
      { path: 'contact', lazy: lazyDefault(() => import('./pages/Contact.jsx')) },
      { path: 'codec-licensing', lazy: lazyDefault(() => import('./pages/CodecLicensing.jsx')) },
      { path: 'terms', lazy: lazyDefault(() => import('./pages/Terms.jsx')) },
      { path: 'privacy', lazy: lazyDefault(() => import('./pages/Privacy.jsx')) },

      // Guides / blog content layer (pre-rendered)
      { path: 'guides', lazy: lazyDefault(() => import('./pages/GuidesIndex.jsx')) },
      {
        path: 'guides/:slug',
        lazy: lazyDefault(() => import('./pages/GuidePage.jsx')),
        getStaticPaths: () => guideSlugs.map((s) => `guides/${s}`),
      },

      // Auth + dashboard (client-only; excluded from SSG via includedRoutes)
      { path: 'auth/login', lazy: lazyDefault(() => import('./pages/auth/LoginPage.jsx')) },
      { path: 'auth/login/email', lazy: lazyDefault(() => import('./pages/auth/EmailLoginPage.jsx')) },
      { path: 'auth/register', lazy: lazyDefault(() => import('./pages/auth/RegisterPage.jsx')) },
      { path: 'auth/callback', lazy: lazyDefault(() => import('./pages/auth/CallbackPage.jsx')) },
      { path: 'auth/forgot', lazy: lazyDefault(() => import('./pages/ForgotPasswordPage.jsx')) },
      { path: 'auth/reset-password', lazy: lazyDefault(() => import('./pages/auth/ResetPasswordPage.jsx')) },
      { path: 'login', lazy: lazyDefault(() => import('./pages/auth/LoginPage.jsx')) },
      { path: 'register', lazy: lazyDefault(() => import('./pages/auth/RegisterPage.jsx')) },
      { path: 'dashboard', lazy: lazyDefault(() => import('./pages/DashboardPage.jsx')) },
      { path: 'conversion-results', lazy: lazyDefault(() => import('./components/ConversionResults.jsx')) },

      // SEO tool pages — one static page per tool slug.
      // NOTE: must stay before the :lang matrix so single-segment tool URLs
      // win the tie against :lang (definition order breaks equal-rank ties).
      {
        path: ':conversionPair',
        lazy: lazyDefault(() => import('./pages/ConvertToolPage.jsx')),
        getStaticPaths: () => toolSlugs,
      },

      // NOTE: the previous /:lang multi-language matrix was removed. Those URLs
      // were never pre-rendered, never linked, and not in the sitemap, and they
      // served untranslated English tool content (duplicate-content risk). UI
      // language still switches client-side via localStorage. Proper i18n SEO
      // would need pre-rendered localized pages plus hreflang tags.
    ],
  },
]
